import React from 'react';
import { Box, Button, Spinner, Text, TextButton } from '@zillow/constellation-10';
import { LoadingConstants } from '@zg-rentals/rental-manager-env';
import type { Property } from '@zg-rentals/rental-manager-env';
import { AdvancedTable } from '@zg-rentals/advanced-table';
import type {
  AdvancedTableFilterConfig,
  AdvancedTableMultiSelectFilter,
  ColumnConfig,
} from '@zg-rentals/advanced-table';
import { useDispatch, useSelector } from 'react-redux';
import UserManagementActions from '../../redux/actions/UserManagementActions';
import DispatchConstants from '../../DispatchConstants';
import { EVENT_IDS } from '../../modal/ClickstreamEvents';

const columnConfig: Array<ColumnConfig<Property>> = [
  {
    width: 5,
    header: 'Property',
    isSortable: true,
    align: 'left',
    renderer: (property: Property) => (
      <div>
        <Text color="text.neutral" asChild>
          <p>
            {property.street}
            {property.unit && ` - ${property.unit}`}
          </p>
        </Text>
        {property.name && (
          <Text css={{ color: 'gray.500' }} asChild>
            <p>{property.name}</p>
          </Text>
        )}
      </div>
    ),
  },
  {
    width: 4,
    dataKey: 'city',
    header: 'City',
    align: 'right',
    isSortable: true,
    renderer: (property: Property) => property.city,
  },
  {
    width: 2,
    dataKey: 'state',
    header: 'State',
    isSortable: true,
    align: 'right',
    renderer: (property: Property) => property.state,
  },
];

type FilterType = {
  [key: string]: AdvancedTableMultiSelectFilter;
};

export interface PropertyPickerTypes {
  onNextStep?: () => void;
  onCancel?: () => void;
  onBack?: () => void;
  isInvitation?: boolean;
}

const PropertyPickerComponent = ({ onNextStep, onCancel, onBack, isInvitation }: PropertyPickerTypes) => {
  // @ts-ignore
  const currentUser = useSelector((state) => state.userManagement.currentUser);
  const [filters, setFilters] = React.useState({} as FilterType);
  const [totalSelectedListings, setTotalSelectedListings] = React.useState(0);
  const dispatch = useDispatch();
  const getSelectedIndexes = (tableData: Array<Property>) => {
    const indexes = new Set<number>();
    const propertySet = new Set<number>(currentUser.properties.map((property: Property) => property.listingId));
    tableData.forEach((property: Property, i: number) => {
      if (propertySet.has(property.listingId)) {
        indexes.add(i);
      }
    });
    return indexes;
  };

  React.useEffect(() => {
    if (isInvitation) {
      dispatch({
        payload: {
          properties: [],
        },
        type: DispatchConstants.USER_MANAGEMENT_GET_CURRENT_USER_PROPERTIES_SUCCESS,
      });
    }
  }, []);

  const handleUpdateProperties = (
    propertyIndexes: Set<number>,
    tableData: { [key: number]: { listingId: number } },
  ) => {
    const newProperties = [];
    const availablePropertyMap: { [key: number]: Property } = {};
    currentUser.availableProperties.forEach((property: Property) => {
      availablePropertyMap[property.listingId] = property;
    });
    for (const index of propertyIndexes) {
      const { listingId } = tableData[index];
      newProperties.push(availablePropertyMap[listingId]);
    }
    dispatch(UserManagementActions.currentUserSetProperties(newProperties));
    setTotalSelectedListings(newProperties.length);
  };

  const renderTableHeader = () => (
    <div className="PropertyPicker-top-banner">
      <Text textStyle="heading-xs" asChild>
        <div>
          {totalSelectedListings
            ? `${totalSelectedListings} ${totalSelectedListings > 1 ? 'properties' : 'property'}`
            : 'Select additional properties'}
        </div>
      </Text>
      {!isInvitation && (
        <Text textStyle="fineprint" asChild>
          <p>*Changes may take a few minutes to sync</p>
        </Text>
      )}
    </div>
  );

  const isLoading = currentUser.propertiesLoadingState !== LoadingConstants.SUCCESS;

  React.useEffect(() => {
    if (currentUser.propertiesLoadingState === LoadingConstants.SUCCESS) {
      const newFilter = { ...filters };
      const states = new Set<string>();
      const cities = new Set<string>();

      currentUser.availableProperties.forEach((property: Property) => {
        states.add(property.state);
      });
      currentUser.availableProperties.forEach((property: Property) => {
        cities.add(property.city);
      });
      newFilter.cities = {
        title: 'City',
        value: Array.from(cities).map((city) => ({
          label: city,
          isChecked: false,
        })),
        type: 'MULTISELECT',
      };
      newFilter.states = {
        title: 'State',
        value: Array.from(states).map((state) => ({
          label: state,
          isChecked: false,
        })),
        type: 'MULTISELECT',
      };
      setFilters(newFilter);
    }
  }, [currentUser.propertiesLoadingState]);

  let filteredData = currentUser.availableProperties.map((p: Property) => ({ ...p }));
  if (currentUser.propertiesLoadingState === LoadingConstants.SUCCESS) {
    if (filters.states) {
      const selectedStates = new Set<string>(
        filters.states.value.filter((filter) => filter.isChecked).map((state) => state.label),
      );
      if (selectedStates.size) {
        filteredData = filteredData.filter((property: Property) => selectedStates.has(property.state));
      }
    }

    if (filters.cities) {
      const selectedCities = new Set<string>(
        filters.cities.value.filter((filter) => filter.isChecked).map((city) => city.label),
      );
      if (selectedCities.size) {
        filteredData = filteredData.filter((property: Property) => selectedCities.has(property.city));
      }
    }
  }

  return (
    <div className="PropertyPicker">
      <div className="PropertyPicker-container">
        {isLoading ? (
          <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <Spinner size="md" />
          </Box>
        ) : (
          <AdvancedTable
            filters={filters}
            onSaveFilters={setFilters as (filters: AdvancedTableFilterConfig) => void}
            defaultSortKey={{
              key: 'street',
              ascending: true,
            }}
            columnConfig={columnConfig}
            data={filteredData}
            selected={getSelectedIndexes}
            onSelectRow={handleUpdateProperties}
            topBannerComponent={renderTableHeader}
            disableSelectAll={true}
          />
        )}
      </div>
      <div className="Questions-footer">
        <TextButton className="Questions-button" onClick={onCancel} data-eventid={EVENT_IDS.CANCEL_ADDING_NEW_USER}>
          Cancel
        </TextButton>
        <div>
          <TextButton className="Questions-button" onClick={onBack}>
            Back
          </TextButton>
          <Button tone="brand" emphasis="filled" onClick={onNextStep}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PropertyPickerComponent;
